import { InstanceBatch } from "../../core/batching/instance_batch.js";
import { AttributeSize, BlendMode } from "../../core/types/webgl_types.js";
import { ShaderBuilder } from "../../shader_builder/shader_builder.js";
import { Vfx2DGlobalAtlas } from "../../core/rendering/textures/vfx_2d_global_atlas.js";
import { WebGLStaticAtlasBuilder } from "../../core/rendering/textures/webgl_static_atlas_bulder.js";

export const UnlockChainOfferSparkLayout = {
  a_position: AttributeSize.Vec2,
  a_size: AttributeSize.Float,
  a_color: AttributeSize.Vec3,
  a_timing: AttributeSize.Vec2,
  a_intensity: AttributeSize.Vec2,
  a_min_uv: AttributeSize.Vec2,
  a_max_uv: AttributeSize.Vec2,
} as const;

export class VfxUnlockChainOfferBatch extends InstanceBatch<typeof UnlockChainOfferSparkLayout>
{
  public static readonly MAX_INSTANCES = 220;

  public constructor()
  {
    super( {
      max_instances: VfxUnlockChainOfferBatch.MAX_INSTANCES,
      primitive_type: WebGL2RenderingContext.TRIANGLES,
      vertices_per_instance: 6,
      blend_mode: BlendMode.Additive,
      layout: UnlockChainOfferSparkLayout,
    } );

    this.buildShader( new ShaderBuilder()
      .withUniform( 'sampler2D', 'u_texture' )
      .withVarying( 'vec2', 'v_uv' )
      .withVarying( 'float', 'v_alpha' )
      .withVarying( 'vec3', 'v_color' )
      .withVertexLogic( `
        float elapsed = max( 0.0, u_time - a_timing.x );
        float t = clamp( elapsed / a_timing.y, 0.0, 1.0 );
        
        position = a_position;
        
        float scale_progress = 1.0 - t;
        size = vec2( a_size * scale_progress );
        
        v_uv = mix( a_min_uv, a_max_uv, uv );
        
        float current_intensity = mix( a_intensity.x, a_intensity.y, scale_progress );
        v_color = a_color * current_intensity;
        v_alpha = ( u_time < a_timing.x ) ? 0.0 : 1.0;
      ` )
      .withFragmentLogic( `
        vec4 tex = texture( u_texture, v_uv );
        float alpha = tex.a * v_alpha;
        fragColor = vec4( alpha * v_color, alpha );
      ` ) );
  }

  protected override onInit( gl: WebGL2RenderingContext ): void
  {
    const atlas_data = Vfx2DGlobalAtlas.getAtlasData();
    const atlas = WebGLStaticAtlasBuilder.createAtlasFromData( gl, atlas_data );
    this.textures = [ { location: 'u_texture', texture: atlas.texture, unit: 0 } ];
  }

  public spawn(
    x: number, y: number,
    size: number,
    r: number, g: number, b: number,
    start_time: number, life_span: number,
    min_intensity: number, max_intensity: number,
    min_x: number, min_y: number, max_x: number, max_y: number
  ): void
  {
    const idx = this.allocateInstanceIndex( start_time, life_span );
    if ( idx === -1 ) return;

    const offset = idx * this.buffer.floats_per_instance;
    const gpu_data = this.buffer.getDataArray();

    gpu_data[offset + this.attr_offset.a_position] = x;
    gpu_data[offset + this.attr_offset.a_position + 1] = y;

    gpu_data[offset + this.attr_offset.a_size] = size;

    gpu_data[offset + this.attr_offset.a_color] = r;
    gpu_data[offset + this.attr_offset.a_color + 1] = g;
    gpu_data[offset + this.attr_offset.a_color + 2] = b;

    gpu_data[offset + this.attr_offset.a_timing] = start_time;
    gpu_data[offset + this.attr_offset.a_timing + 1] = life_span;

    gpu_data[offset + this.attr_offset.a_intensity] = min_intensity;
    gpu_data[offset + this.attr_offset.a_intensity + 1] = max_intensity;

    gpu_data[offset + this.attr_offset.a_min_uv] = min_x;
    gpu_data[offset + this.attr_offset.a_min_uv + 1] = min_y;

    gpu_data[offset + this.attr_offset.a_max_uv] = max_x;
    gpu_data[offset + this.attr_offset.a_max_uv + 1] = max_y;
  }

  protected override updateInstance(): boolean
  {
    return true;
  }
}
